import React, { useCallback, useEffect } from 'react';

import Link from 'next/link';
import { useSearchParams } from 'next/navigation';

import Popover from '@repo/editor/src/components/Popover/Popover';
import { PopoverPlacement } from '@repo/editor/src/components/Popover/types';

import { convert } from 'components/CommentBox/utils';
import { buildPublicProfilePath } from 'global/urlUtils';

import { SuggestionList } from '../../CommentBox/SuggestionList/SuggestionList';
import { Mention } from '../../CommentBox/types';

import { StyledMention } from './styles';
import { suggestionsTheme } from './theme';
import { CommentItemMentionProps } from './types';

export const CommentItemMention: React.FC<CommentItemMentionProps> = ({
  content,
  wrapperRef,
  getUserBySlug,
  closeParent,
}) => {
  const [user, setUser] = React.useState<Mention | null>(null);
  const searchParams = useSearchParams();

  const getConvertedUser = useCallback(
    async (slug: string) => {
      const user = await getUserBySlug(slug);
      return convert(user);
    },
    [getUserBySlug]
  );

  useEffect(() => {
    const abortController = new AbortController();
    const getInternal = async (): Promise<void> => {
      const user = await getConvertedUser(content);
      if (!abortController.signal.aborted) {
        setUser(user);
      }
    };

    void getInternal();
    return () => abortController.abort();
  }, [content, getConvertedUser]);

  const handleSamePageClick = (
    e: React.PointerEvent | React.MouseEvent
  ): void => {
    const isCurrentPage = user?.slug === searchParams?.get('slug');
    if (!isCurrentPage) {
      return;
    }
    e.preventDefault();
    closeParent && closeParent();
  };

  return (
    <Popover
      hideTip
      useHover
      container={wrapperRef}
      content={
        user ? (
          <Link href={buildPublicProfilePath(user.slug)}>
            <SuggestionList
              suggestions={[user]}
              theme={suggestionsTheme}
              onClick={handleSamePageClick}
            />
          </Link>
        ) : (
          <SuggestionList
            loading={true}
            suggestions={[]}
            theme={suggestionsTheme}
          />
        )
      }
      placement={PopoverPlacement.Bottom}
      showHeader={false}
      theme={{ padding: '0px' }}
      width="contain"
    >
      <StyledMention
        onTouchEnd={(e: React.TouchEvent<HTMLSpanElement>): void => {
          e.preventDefault();
        }}
      >
        <Link legacyBehavior href={buildPublicProfilePath(user?.slug)}>
          <a onClick={handleSamePageClick}>{content}</a>
        </Link>
      </StyledMention>
    </Popover>
  );
};
